// === FEATURE: HolyBumperButton ===
// The reverent invitation to the holiest chamber of this sacred site: the
// Seraphim Bumper (the full-screen immersive music experience — "Above him stood
// the seraphim"). This is the ORNATE, DIVINE portal-button that calls the
// visitor up and in.
//
// Design intent: a button worthy of God. Radiant gold, a glowing aureole/halo,
// soft godrays/light-shafts behind it, ornate sacred framing, a gentle holy
// shimmer + slow pulse, reverent dot/serif typography. It leans into
// biblically-accurate-seraphim imagery — concentric RINGS / WINGS OF EYES (which
// ties directly into this site's ever-present eye motif) crowned by a halo — so
// it is divine AND a little uncanny, cohering with the industrial / CRT / eye
// aesthetic of the Ambulation Zone.
//
// Contract with App:
//   props.onEnter — called on click (and Enter/Space). App passes
//                   () => setBumperOpen(true), which drives <AngelicBumperView/>.
//
// Motion: a slow pulse + shimmer + drifting godrays + blinking seraphim eyes by
// default; all calmed under @media (prefers-reduced-motion: reduce).

import * as React from "react";
import { useCallback } from "react";
import "./holybumperbutton.css";

// One ring of seraphim eyes — small lids that slowly blink. Rendered as decor
// (aria-hidden); count is the number of eyes spaced evenly around the ring.
function EyeRing({ className, count }) {
  const eyes = [];
  for (let i = 0; i < count; i++) {
    const angle = (360 / count) * i;
    // Stagger each eye's blink so the ring shimmers like a living wing of eyes.
    const delay = ((i * 0.37) % 2.2).toFixed(2);
    eyes.push(
      <span
        key={i}
        className="hbb-eye"
        style={{
          transform: `rotate(${angle}deg) translateY(var(--hbb-eye-radius))`,
          animationDelay: `${delay}s`,
        }}
      >
        <span className="hbb-eye-lid">
          <span className="hbb-eye-iris" />
        </span>
      </span>
    );
  }
  return (
    <span className={"hbb-eyering " + className} aria-hidden="true">
      {eyes}
    </span>
  );
}

function HolyBumperButton({ onEnter }) {
  const enter = useCallback(() => {
    if (typeof onEnter === "function") onEnter();
  }, [onEnter]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        enter();
      }
    },
    [enter]
  );

  return (
    <div className="hbb-banner" role="presentation">
      {/* Soft godrays / shafts of light fanning out behind the portal. */}
      <div className="hbb-godrays" aria-hidden="true" />

      <button
        type="button"
        className="hbb-portal"
        onClick={enter}
        onKeyDown={onKeyDown}
        aria-label="Enter the Seraphim Bumper"
        title="Enter the Seraphim Bumper"
      >
        {/* The aureole / halo: stacked radiant rings of gold light. */}
        <span className="hbb-aureole" aria-hidden="true" />
        <span className="hbb-halo" aria-hidden="true" />

        {/* Seraphim: concentric rings/wings of ever-watching eyes. Biblically
            accurate, divine, and a little uncanny — and a direct nod to the
            site's recurring eye motif. */}
        <EyeRing className="hbb-eyering--outer" count={16} />
        <EyeRing className="hbb-eyering--inner" count={10} />

        {/* The reverent label. */}
        <span className="hbb-label">
          <span className="hbb-seal" aria-hidden="true">
            &#10038;
          </span>
          <span className="hbb-words">
            <span className="hbb-kicker">above him stood the seraphim</span>
            <span className="hbb-title">ENTER&nbsp;THE&nbsp;ANGELIC&nbsp;BUMPER</span>
          </span>
          <span className="hbb-seal" aria-hidden="true">
            &#10038;
          </span>
        </span>

        {/* A gentle holy shimmer that sweeps across the gold. */}
        <span className="hbb-shimmer" aria-hidden="true" />
      </button>
    </div>
  );
}

export default HolyBumperButton;
